import { FRONT_ARMOR_MULTIPLIER, PLAYER_MAX_HP, PLAYER_SPECS } from './constants';
import { BOSS_VARIANT_PROFILES } from '../content/enemies';
import type { GameModeConfig, PlayerTankClass, TankEntity } from './types';

export const MAX_PLAYER_UPGRADE_LEVEL = 4;

const PLAYER_UPGRADE_STATS = [
  { maxHp: PLAYER_MAX_HP, fireRateMultiplier: 1, bulletSpeedBonus: 0, damage: 1, frontArmorMultiplier: FRONT_ARMOR_MULTIPLIER },
  { maxHp: PLAYER_MAX_HP + 1, fireRateMultiplier: 0.96, bulletSpeedBonus: 20, damage: 1, frontArmorMultiplier: 0.42 },
  { maxHp: PLAYER_MAX_HP + 1, fireRateMultiplier: 0.92, bulletSpeedBonus: 38, damage: 1.35, frontArmorMultiplier: 0.38 },
  { maxHp: PLAYER_MAX_HP + 2, fireRateMultiplier: 0.86, bulletSpeedBonus: 56, damage: 1.55, frontArmorMultiplier: 0.35 },
] as const;

export type PlayerUpgradeStats = typeof PLAYER_UPGRADE_STATS[number];

export const DEFAULT_PLAYER_TANK_CLASS: PlayerTankClass = 'standard';

export const PLAYER_TANK_CLASS_SPECS: Record<PlayerTankClass, {
  maxHpBonus: number;
  maxSpeedMultiplier: number;
  accelerationMultiplier: number;
  fireRateMultiplier: number;
  bulletSpeedBonus: number;
  damageMultiplier: number;
  frontArmorMultiplier: number;
}> = {
  standard: {
    maxHpBonus: 0,
    maxSpeedMultiplier: 1,
    accelerationMultiplier: 1,
    fireRateMultiplier: 1,
    bulletSpeedBonus: 0,
    damageMultiplier: 1,
    frontArmorMultiplier: 1,
  },
  assault: {
    maxHpBonus: 0,
    maxSpeedMultiplier: 1.18,
    accelerationMultiplier: 1.14,
    fireRateMultiplier: 0.9,
    bulletSpeedBonus: 16,
    damageMultiplier: 1,
    frontArmorMultiplier: 1.1,
  },
  heavy: {
    maxHpBonus: 1,
    maxSpeedMultiplier: 0.88,
    accelerationMultiplier: 0.92,
    fireRateMultiplier: 1.14,
    bulletSpeedBonus: -20,
    damageMultiplier: 1.08,
    frontArmorMultiplier: 0.88,
  },
};

export function getPlayerUpgradeStats(player: TankEntity): PlayerUpgradeStats {
  const level = Math.max(1, Math.min(MAX_PLAYER_UPGRADE_LEVEL, player.upgradeLevel));
  return PLAYER_UPGRADE_STATS[level - 1];
}

export function normalizePlayerTankClass(value: unknown): PlayerTankClass {
  return value === 'assault' || value === 'heavy' ? value : DEFAULT_PLAYER_TANK_CLASS;
}

export function getPlayerTankClassSpec(tank: TankEntity): typeof PLAYER_TANK_CLASS_SPECS[PlayerTankClass] {
  return PLAYER_TANK_CLASS_SPECS[normalizePlayerTankClass(tank.playerTankClass)];
}

export function applyPlayerUpgradeStats(player: TankEntity): void {
  const stats = getPlayerUpgradeStats(player);
  const classSpec = getPlayerTankClassSpec(player);
  const spec = PLAYER_SPECS[(player.playerNum ?? 1) - 1];
  player.maxHp = Math.max(1, stats.maxHp + classSpec.maxHpBonus);
  player.hp = Math.min(player.hp, player.maxHp);
  player.baseFireRate = spec.fireRate * stats.fireRateMultiplier * classSpec.fireRateMultiplier;
  player.fireRate = player.rapidTimer > 0 ? player.baseFireRate * 0.55 : player.baseFireRate;
  player.bulletSpeed = spec.bulletSpeed + stats.bulletSpeedBonus + classSpec.bulletSpeedBonus;
}

export function upgradePlayerTank(player: TankEntity): void {
  const before = player.upgradeLevel;
  player.upgradeLevel = Math.min(MAX_PLAYER_UPGRADE_LEVEL, player.upgradeLevel + 1);
  applyPlayerUpgradeStats(player);
  if (player.upgradeLevel > before) {
    player.specialShotCounter = 0;
    player.hp = Math.min(player.maxHp, player.hp + 1.5);
  }
}

export function downgradePlayerOnDeath(player: TankEntity, mode: GameModeConfig): void {
  if (!mode.loseUpgradeOnDeath || player.upgradeLevel <= mode.initialPlayerLevel) return;
  player.upgradeLevel -= 1;
  player.specialShotCounter = 0;
  applyPlayerUpgradeStats(player);
}

export function getTankBulletDamage(tank: TankEntity): number {
  if (tank.kind === 'boss') return BOSS_VARIANT_PROFILES[tank.bossVariant ?? 'siege'].bulletDamage;
  if (tank.kind === 'heavy') return 2.25;
  if (tank.team === 'player') return getPlayerUpgradeStats(tank).damage * getPlayerTankClassSpec(tank).damageMultiplier;
  return 1;
}
